import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
const text = {
  question: 'Vad händer om ingenting förändras?',
  line1: 'Initiativ tappar fart.',
  line2: 'Möten ger inte förflyttning.',
  line3: 'Och om ett år sitter du i exakt samma situation — fast lite tröttare.'
};
const variants = [{
  id: 1,
  label: 'Variant 1 — Editorial, ingen box',
  description: 'Stor typografi, terracotta på nyckelord, ingen ram'
}, {
  id: 2,
  label: 'Variant 2 — Vänsterkantlinje',
  description: 'Minimal 3px linje, ingen bakgrund, journalistisk känsla'
}, {
  id: 3,
  label: 'Variant 3 — Tre kolumner med siffror',
  description: 'Uppdelat i tre korta påståenden med 01/02/03'
}, {
  id: 4,
  label: 'Variant 4 — Mörkt kontrastblock',
  description: 'Ink-bakgrund, vit text, dramatiskt avbrott'
}];
export function DesignPreview() {
  const [selected, setSelected] = useState<number | null>(null);
  return <div className="min-h-screen bg-background font-sans" data-id="element-252">
      {/* Nav */}
      <div className="sticky top-0 z-50 bg-background/90 backdrop-blur-sm border-b border-border/50 px-6 py-4" data-id="element-253">
        <div className="max-w-4xl mx-auto flex items-center justify-between" data-id="element-254">
          <Link to="/omrade-1" className="flex items-center gap-2 text-muted hover:text-ink transition-colors text-sm group" data-id="element-255">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-256" />
            Tillbaka till Område 1
          </Link>
          <span className="text-xs font-medium tracking-widest uppercase text-muted" data-id="element-257">
            Designförslag — Callout-text
          </span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-16 space-y-20" data-id="element-258">
        {/* VARIANT 1 — Editorial */}
        <div data-id="element-259">
          <div className="flex items-start justify-between mb-6" data-id="element-260">
            <div data-id="element-261">
              <span className="text-xs font-bold tracking-widest uppercase text-terracotta" data-id="element-262">
                Variant 1
              </span>
              <p className="text-sm text-muted mt-1" data-id="element-263">
                Editorial, ingen box — stor typografi med terracotta-accent
              </p>
            </div>
            <button onClick={() => setSelected(1)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${selected === 1 ? 'bg-terracotta text-white' : 'border border-border text-muted hover:text-ink hover:border-ink'}`} data-id="element-264">
              {selected === 1 && <Check className="w-3.5 h-3.5" data-id="element-265" />}
              {selected === 1 ? 'Vald' : 'Välj denna'}
            </button>
          </div>

          {/* Preview in context */}
          <div className="bg-card/40 rounded-2xl p-10" data-id="element-266">
            <div className="mt-10 pl-0 max-w-2xl" data-id="element-267">
              <p className="text-2xl md:text-3xl font-light text-ink leading-snug" data-id="element-268">
                <span className="text-terracotta font-medium" data-id="element-269">
                  Vad händer om ingenting förändras?
                </span>{' '}
                Initiativ tappar fart. Möten ger inte förflyttning.{' '}
                <span className="text-muted" data-id="element-270">
                  Och om ett år sitter du i exakt samma situation — fast lite
                  tröttare.
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* VARIANT 2 — Left border */}
        <div data-id="element-271">
          <div className="flex items-start justify-between mb-6" data-id="element-272">
            <div data-id="element-273">
              <span className="text-xs font-bold tracking-widest uppercase text-terracotta" data-id="element-274">
                Variant 2
              </span>
              <p className="text-sm text-muted mt-1" data-id="element-275">
                Vänsterkantlinje — minimal, journalistisk
              </p>
            </div>
            <button onClick={() => setSelected(2)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${selected === 2 ? 'bg-terracotta text-white' : 'border border-border text-muted hover:text-ink hover:border-ink'}`} data-id="element-276">
              {selected === 2 && <Check className="w-3.5 h-3.5" data-id="element-277" />}
              {selected === 2 ? 'Vald' : 'Välj denna'}
            </button>
          </div>

          <div className="bg-card/40 rounded-2xl p-10" data-id="element-278">
            <div className="mt-10 border-l-[3px] border-terracotta pl-6 max-w-2xl" data-id="element-279">
              <p className="text-ink text-lg leading-relaxed" data-id="element-280">
                Vad händer om ingenting förändras? Initiativ tappar fart. Möten
                ger inte förflyttning. Och om ett år sitter du i exakt samma
                situation — fast lite tröttare.
              </p>
            </div>
          </div>
        </div>

        {/* VARIANT 3 — Three columns */}
        <div data-id="element-281">
          <div className="flex items-start justify-between mb-6" data-id="element-282">
            <div data-id="element-283">
              <span className="text-xs font-bold tracking-widest uppercase text-terracotta" data-id="element-284">
                Variant 3
              </span>
              <p className="text-sm text-muted mt-1" data-id="element-285">
                Tre kolumner med siffror — lätt att skanna
              </p>
            </div>
            <button onClick={() => setSelected(3)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${selected === 3 ? 'bg-terracotta text-white' : 'border border-border text-muted hover:text-ink hover:border-ink'}`} data-id="element-286">
              {selected === 3 && <Check className="w-3.5 h-3.5" data-id="element-287" />}
              {selected === 3 ? 'Vald' : 'Välj denna'}
            </button>
          </div>

          <div className="bg-card/40 rounded-2xl p-10" data-id="element-288">
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-289">
              {[{
              num: '01',
              text: 'Initiativ tappar fart.'
            }, {
              num: '02',
              text: 'Möten ger inte förflyttning.'
            }, {
              num: '03',
              text: 'Om ett år: samma situation — fast lite tröttare.'
            }].map(item => <div key={item.num} className="flex flex-col gap-3" data-id="element-290">
                  <span className="text-3xl font-light text-terracotta/30 leading-none" data-id="element-291">
                    {item.num}
                  </span>
                  <p className="text-ink font-medium leading-snug" data-id="element-292">
                    {item.text}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* VARIANT 4 — Dark block */}
        <div data-id="element-293">
          <div className="flex items-start justify-between mb-6" data-id="element-294">
            <div data-id="element-295">
              <span className="text-xs font-bold tracking-widest uppercase text-terracotta" data-id="element-296">
                Variant 4
              </span>
              <p className="text-sm text-muted mt-1" data-id="element-297">
                Mörkt kontrastblock — dramatiskt avbrott
              </p>
            </div>
            <button onClick={() => setSelected(4)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${selected === 4 ? 'bg-terracotta text-white' : 'border border-border text-muted hover:text-ink hover:border-ink'}`} data-id="element-298">
              {selected === 4 && <Check className="w-3.5 h-3.5" data-id="element-299" />}
              {selected === 4 ? 'Vald' : 'Välj denna'}
            </button>
          </div>

          <div className="bg-card/40 rounded-2xl p-10" data-id="element-300">
            <div className="mt-10 bg-ink rounded-2xl px-8 py-7 max-w-2xl" data-id="element-301">
              <p className="text-white/90 text-lg leading-relaxed" data-id="element-302">
                Vad händer om ingenting förändras?{' '}
                <span className="text-white font-medium" data-id="element-303">
                  Initiativ tappar fart. Möten ger inte förflyttning.
                </span>{' '}
                <span className="text-white/60" data-id="element-304">
                  Och om ett år sitter du i exakt samma situation — fast lite
                  tröttare.
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Selection summary */}
        {selected && <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} className="sticky bottom-6 bg-ink text-white rounded-2xl px-8 py-5 flex items-center justify-between shadow-xl" data-id="element-305">
            <div data-id="element-306">
              <p className="font-medium" data-id="element-307">Variant {selected} vald</p>
              <p className="text-white/60 text-sm" data-id="element-308">
                {variants.find(v => v.id === selected)?.description}
              </p>
            </div>
            <Link to="/omrade-1" className="bg-terracotta hover:bg-terracotta/90 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors" data-id="element-309">
              Säg till mig vilken → jag uppdaterar
            </Link>
          </motion.div>}
      </div>
    </div>;
}